import { Link } from 'ui/links';
import { Title, Header, MainContent } from '../styled';
import { ProjectBreakdownLink } from '../ProjectBreakdownLink';
import { LoadingCard } from '../LoadingCard';
import { CaptionText } from '../MetricTile.styled';
import {
  useGetMonthlyJvtUsage,
  useGetMonthlyLimitsWithRollover,
  //useGetOrgUsageLimit,
  //useGetYearlyJvtUsage,
} from './JourneyEventsCardUtils';
import {
  AnalyticsCardLabel,
  AnalyticsCardLabelWrapper,
  AnalyticsCardWrapper,
  JourneyEventsContainer,
  MetricCountWrapper,
  MetricsWrapper,
  MonthlyMetricCountLabel,
  ProjectsHeaderWrapper,
  ProjectsListWrapper,
  ProjectsRowsWrapper,
  StyledEventsCount,
  StyledHeaderColumn,
  StyledMetricCount,
  StyledProjectName,
  StyledRow,
  YearlyMetricCountLabel,
} from './JourneyEventsCard.styled';

const analyticsLabels = {
  monthlyLabel: 'Total events by month',
  yearlyLabel: 'Total events by year',
};

export const JourneyEventsCard = () => {
  //const yearlyUsage = useGetYearlyJvtUsage();
  const monthlyUsage = useGetMonthlyJvtUsage();
  const { monthlyLimit } = useGetMonthlyLimitsWithRollover();
  /* yearly limit & yearly card will be added back when designs are updated
  const { pickOrgMetric } = useBillingQueryContext();
  const jvtOrgMetric = pickOrgMetric(JVT_COUNT);
  const { metricUsageLimit } = useGetOrgUsageLimit(jvtOrgMetric.organizationId);
  */

  const projectsJvtCount = monthlyUsage.jvtCountPerProject;
  /* show the projects with the most usage first */
  projectsJvtCount.sort((a, b) => b.value - a.value);

  return /*yearlyUsage.isLoading || */ monthlyUsage.isLoading ? (
    <LoadingCard />
  ) : (
    <JourneyEventsContainer data-test="journey-events-metrics">
      <Header>
        <Title>Journey events</Title>
        <div>
          <ProjectBreakdownLink to="/payments/info/journeyEventsUsage" />
        </div>
      </Header>
      <MetricsWrapper>
        <AnalyticsCard
          monthly
          label={analyticsLabels.monthlyLabel}
          caption={monthlyUsage.displayDateRange}
          count={monthlyUsage?.totalJvtCountPerOrg}
          limit={monthlyLimit}
        />
        {/*<AnalyticsCard
          label={analyticsLabels.yearlyLabel}
          caption={yearlyUsage.displayDateRange}
          count={yearlyUsage.totalJvtCountPerOrg}
          limit={metricUsageLimit.combinedLimit}
        />*/}
      </MetricsWrapper>
      <MainContent>
        <ProjectsTable data={projectsJvtCount} />
      </MainContent>
    </JourneyEventsContainer>
  );
};

type ProjectNameLinkRowProps = {
  id: number;
  name: string;
};

export const getJourneyEventsUsageForProjectLink = (id: string | number) =>
  `/payments/info/journeyEventsUsage/${id}`;

const ProjectNameLinkRow = ({ id, name }: ProjectNameLinkRowProps) => {
  /** Will be linking to the project journey event usage view
   * /payments/info/journeyEventsUsage/:projectId
   * placeholder for now
   */
  const to = getJourneyEventsUsageForProjectLink(id);

  return (
    <Link data-test="journey-event-project-breakdown-link" useReactRouter href={`${to}`}>
      {name}
    </Link>
  );
};

type ProjectJvtCount = {
  id: number;
  name: string;
  value: number;
};

type ProjectsTableProps = {
  data: ProjectJvtCount[];
};

const getDataSum = (data: ProjectJvtCount[]) => {
  return data.reduce((n, { value }) => n + value, 0);
};

const ProjectsTable = ({ data }: ProjectsTableProps) => {
  const topData = data.slice(0, 5);
  const topDataSum = getDataSum(topData);
  const allDataSum = getDataSum(data);
  return (
    <ProjectsListWrapper>
      <ProjectsHeaderWrapper>
        <StyledHeaderColumn>PROJECT</StyledHeaderColumn>
        <StyledHeaderColumn>EVENTS TRACKED IN A MONTH</StyledHeaderColumn>
      </ProjectsHeaderWrapper>
      <ProjectsRowsWrapper>
        {topData.map((e) => (
          <StyledRow key={e.id}>
            <StyledProjectName>
              <ProjectNameLinkRow id={e.id} name={e.name} />
            </StyledProjectName>
            <StyledEventsCount>{e.value.toLocaleString()}</StyledEventsCount>
          </StyledRow>
        ))}
        <StyledRow>
          <StyledProjectName>All other projects</StyledProjectName>
          <StyledEventsCount>{(allDataSum - topDataSum).toLocaleString()}</StyledEventsCount>
        </StyledRow>
      </ProjectsRowsWrapper>
    </ProjectsListWrapper>
  );
};

/* Analytics card */

type AnalyticsCardProps = {
  label: string;
  caption: string;
  count: number;
  limit: number;
  monthly?: boolean;
};

const AnalyticsCard = ({ label, caption, count, limit, monthly }: AnalyticsCardProps) => {
  const formatedCount = count.toLocaleString();
  const formattedLimit = (limit / 1000000).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const formattedUsage = ((count / limit) * 100).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });
  return (
    <AnalyticsCardWrapper>
      <AnalyticsCardLabelWrapper>
        <AnalyticsCardLabel>{label}</AnalyticsCardLabel>
        <CaptionText>{caption}</CaptionText>
      </AnalyticsCardLabelWrapper>
      <MetricCountWrapper>
        <StyledMetricCount>
          {monthly ? (
            <MonthlyMetricCountLabel>{formatedCount}</MonthlyMetricCountLabel>
          ) : (
            <YearlyMetricCountLabel>{formatedCount}</YearlyMetricCountLabel>
          )}
          <CaptionText>{`/ ${formattedLimit}M per ${monthly ? 'month' : 'year'} (${formattedUsage}%)`}</CaptionText>
        </StyledMetricCount>
      </MetricCountWrapper>
    </AnalyticsCardWrapper>
  );
};
